import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  TableRow,
  TableCell,
  Avatar,
  ListItemText,
  Typography,
  Tooltip,
  CircularProgress,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ComercialInterfaceHeader from "../../../../../components/comercial/ComercialInterfaceHeader";
import ComercialDrawer from "../../../../../components/pet-beauty/CormercialDrawer";
import { IStore } from "../../../../../services/api/Store/types";
import { cleanLocalStorage } from "../../../../../utils/cleanLocalStorage";
import defaultStoreImage from "../../../../../assets/svg/defaultStore.svg";
import useFetchManagerStores from "../../../../../hooks/entities/useFetchManagerStores";
import { handleDeleteStore, handleSelectStore } from "./functions";
import useManagerStores from "../../../../../hooks/context-hooks/useManagerStores";
import ComercialTable from "../../../../../components/comercial/ComercialTable";
import { sharedStyles } from "../../../../../styles/shared";
import { useTranslation } from "react-i18next";

const Stores = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  cleanLocalStorage();

  const managerStoresContext = useManagerStores();
  const { fetchStores, loadingManagerStores } = useFetchManagerStores();

  const handleSeeStoreDetailsClick = (store: IStore) => {
    handleSelectStore(store);
    navigate(`/stores/details?id=${store.id}`);
  };

  const handleEditStoreClick = (store: IStore) => {
    handleSelectStore(store);
    navigate(`/stores/edit?id=${store.id}`);
  };

  const handleDeleteManagerStore = (store: IStore) => {
  const confirmMessage = t("storePage.management.deleteConfirmation", { name: store.name });
    handleDeleteStore(store, confirmMessage).then(() => fetchStores());};

  const storesTableHeader = (
    <TableRow>
      <TableCell align="left">{t("storePage.management.table.store")}</TableCell>
      <TableCell align="right">{t("storePage.management.table.actions")}</TableCell>
    </TableRow>
  );

  const storesTableBody = (
    <>
      {managerStoresContext.stores?.length !== 0 ? (
        managerStoresContext.stores?.map((store) => (
          <TableRow
            key={store.id}
            sx={{
              "&:last-child td, &:last-child th": { border: 0 },
            }}
          >
            <TableCell
              component="th"
              sx={{ ...sharedStyles.horizontalContent }}
              align="left"
            >
              <Avatar
                src={store.imagePreview[0] ?? defaultStoreImage}
                sx={sharedStyles.tableAvatarStyle}
              />

              <ListItemText
                primary={store.name}
                secondary={store?.CNPJ}
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </TableCell>

            <TableCell align="right">
               <Tooltip title={t("storePage.management.tooltips.details")}>
                <IconButton
                  color="primary"
                  onClick={() => handleSeeStoreDetailsClick(store)}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            <Tooltip title={t("storePage.management.tooltips.edit")}>
                <IconButton
                  color="primary"
                  onClick={() => handleEditStoreClick(store)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("storePage.management.tooltips.delete")}>
                <IconButton
                  color="primary"
                  onClick={() => handleDeleteManagerStore(store)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell>
            <Typography color="text.secondary" variant="body2">
              {t("storePage.management.empty")}
            </Typography>
          </TableCell>
        </TableRow>
      )}
    </>
  );

  const renderStoresPageContent = (
    <Box>
    <ComercialInterfaceHeader
        title={t("storePage.management.title")}
        actionNode={
          <Button
            onClick={() => navigate("/stores/add")}
            color="primary"
            variant="contained"
            disableElevation
          >
            <AddBusinessIcon />
          </Button>
        }
      />

      <Box width="100%">
        {!loadingManagerStores ? (
          <ComercialTable header={storesTableHeader} body={storesTableBody} />
        ) : (
          <Box sx={styles.loadingContainer}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={sharedStyles.sharedMainContainer}>
      <ComercialDrawer content={renderStoresPageContent} />
    </Box>
  );
};

export default Stores;

const styles = {
  loadingContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    margin: "50px 0px",
  },
};
